/**
 * VEXOS Executive — the executive's read of the recurring VEXOS business.
 *
 * One fetch (GET /vexos-executive/overview, routers/vexos_executive.py) feeds
 * three views: the revenue forecast, the subscription book grouped by
 * certainty tier, and the live VEXOS project list. Signed and unsigned money
 * never share a figure — "Forecasted" (On the Horizon) is always its own
 * series, its own KPI, its own badge.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiClient, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import ForecastPanel from "./vexos-executive/ForecastPanel";
import SubscriptionsPanel from "./vexos-executive/SubscriptionsPanel";
import ProjectsPanel from "./vexos-executive/ProjectsPanel";
import { formatCompact, formatCurrency } from "./vexos-executive/format";

const TABS = [
  { key: "forecast", label: "Forecast" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "projects", label: "Projects" },
];

function Kpi({ label, value, subtext, alert }) {
  return (
    <Card className="min-w-0">
      <CardContent className="p-4 sm:p-5">
        <p className="micro-label mb-1.5 truncate">{label}</p>
        <p className={`text-2xl xl:text-3xl tabular truncate ${alert ? "text-terracotta-700" : ""}`}>
          {value}
        </p>
        {subtext && (
          <p className="text-[11px] text-muted-foreground mt-1 truncate">{subtext}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function VexosExecutive() {
  const { user } = useAuth();
  const canAccess = ["COO", "Admin", "Accountant", "VEXOS Executive"].includes(user?.role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("forecast");

  const fetchData = useCallback(async (quiet = false) => {
    if (!canAccess) return;
    if (!quiet) setLoading(true);
    try {
      const res = await apiClient.get("/vexos-executive/overview");
      setData(res.data);
    } catch (e) {
      toast.error("Couldn't load the VEXOS executive view");
    }
    setLoading(false);
  }, [canAccess]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!canAccess) {
    return (
      <div className="max-w-lg mx-auto mt-24 text-center">
        <Lock className="w-5 h-5 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
        <h1 className="text-2xl font-light mb-2">Executive access only</h1>
        <p className="text-sm text-muted-foreground">
          This view carries company financials and is limited to COO, Admin and
          Accountant roles.
        </p>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const counts = kpis.counts || {};
  const pipelineMrr = (kpis.mrr_ready || 0) + (kpis.mrr_signed || 0);

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="micro-label mb-1">Executive</p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">VEXOS Executive</h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {kpis.live_project_count ?? "—"} live projects · annual platform
            subscriptions, signed and forecasted
            {kpis.from_quote_count > 0 && (
              <span> · {kpis.from_quote_count} read from a proposal</span>
            )}
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="py-24 text-center text-sm text-muted-foreground">
          Reading the recurring book…
        </div>
      ) : !data ? null : (
        <>
          {/* KPI row — signed money and forecasted money never share a card */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-4 mb-8">
            <Kpi
              label="Billing now"
              value={`${formatCompact(kpis.mrr_active)}/mo`}
              subtext={`${counts.active || 0} active · ${formatCompact(kpis.arr_active)} ARR run-rate`}
            />
            <Kpi
              label="Signed pipeline"
              value={`${formatCompact(pipelineMrr)}/mo`}
              subtext={`${counts.ready || 0} ready to bill · ${counts.signed || 0} in delivery`}
            />
            <Kpi
              label="Forecasted"
              value={`${formatCompact(kpis.mrr_forecasted)}/mo`}
              subtext={`${counts.forecasted || 0} on the horizon — not signed`}
            />
            <Kpi
              label="Signed ARR potential"
              value={formatCompact(kpis.arr_potential_signed)}
              subtext="active + ready + signed, at 12 months"
            />
            <Kpi
              label="Collected to date"
              value={formatCompact(kpis.collected_to_date)}
              subtext={
                kpis.outstanding_active > 0
                  ? `${formatCurrency(kpis.outstanding_active)} still open on active terms`
                  : "active terms fully settled"
              }
            />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-border mb-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm -mb-px border-b transition-colors duration-250 ease-brand ${
                  tab === t.key
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
                {t.key === "subscriptions" && (
                  <span className="ml-1.5 text-xs opacity-50 tabular">
                    {data.subscriptions?.length || 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === "forecast" && (
            <ForecastPanel
              forecast={data.forecast}
              unscheduled={data.unscheduled}
              kpis={kpis}
            />
          )}
          {tab === "subscriptions" && (
            <SubscriptionsPanel
              subscriptions={data.subscriptions || []}
              gaps={data.gaps}
              onRefresh={() => fetchData(true)}
            />
          )}
          {tab === "projects" && <ProjectsPanel projects={data.projects || []} />}
        </>
      )}
    </div>
  );
}
